import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { RootState } from 'store/configureStore';
import CustomTable from 'components/Tables/CustomTable';
import InternalControlLayout from 'components/Layouts/InternalControlLayout';
import { getOperationalEffectivenessColumns } from 'constants/operationalEffectivenessColumns';
import ApiService from 'utils/ApiCalls';
import { ControlRow, QueryScope } from 'store/ducks/InternalControls/types';
import { internalControlsOp } from 'store/ducks/InternalControls';
import qs from 'qs';
import { useLocation } from 'react-router-dom';
import { useQueryParams, withDefault, ArrayParam } from 'use-query-params';
import dayjs from 'dayjs';
import * as Styled from '../../RiskControl.styled';

const OperationalEffectivenessTesting: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { drawTable, selectedEntityId, lang } = useSelector(
    (state: RootState) => ({
      drawTable: state.internalControls.drawTable,
      selectedEntityId: state.internalControls.selectedEntityId,
      lang: state.global.lang
    })
  );

  useEffect(() => {
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);
  const [controls, setControls] = useState<ControlRow[]>([]);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const originalControlsRef = useRef<ControlRow[]>([]);
  const [newControlIds, setNewControlIds] = useState<Set<number>>(new Set());
  const [isDirty, setIsDirty] = useState(false);
  const [errorDisplay, setErrorDisplay] = useState<string>('');
  const dispatch = useDispatch();

  // Simple tracking function that marks data as dirty
  const trackingSetControls = (
    newControls: ControlRow[] | ((prev: ControlRow[]) => ControlRow[])
  ) => {
    if (typeof newControls === 'function') {
      setControls((prev) => {
        const result = newControls(prev);
        setIsDirty(true);
        return result;
      });
    } else {
      setControls(newControls);
      setIsDirty(true);
    }
  };

  useEffect(() => {
    setSaveDisabled(!isDirty && newControlIds.size === 0);
    dispatch(
      internalControlsOp.setHasChanges(isDirty || newControlIds.size > 0)
    );
  }, [isDirty, newControlIds, dispatch]);

  const [performerToE, setPerformerToE] = useState<
    { label: string; value: number }[]
  >([]);

  const [operatingEffectiveness, setOperatingEffectiveness] = useState<
    { label: string; value: number }[]
  >([]);

  const [designEffectiveness, setDesignEffectiveness] = useState<
    { label: string; value: number }[]
  >([]);

  const [testingMethod, setTestingMethod] = useState<
    { label: string; value: number }[]
  >([]);

  const [testObjectiveness, setTestObjectiveness] = useState<
    { label: string; value: number }[]
  >([]);

  const [testType, setTestType] = useState<{ label: string; value: number }[]>(
    []
  );

  const [testResult, setTestResult] = useState<
    { label: string; value: number }[]
  >([]);

  const [evidenceType, setEvidenceType] = useState<
    { label: string; value: number }[]
  >([]);

  const location = useLocation();

  const queryParams = qs.parse(location.search, {
    ignoreQueryPrefix: true
  });

  const FiltersParam = withDefault(ArrayParam, []);

  const [query, setQuery] = useQueryParams({
    group: FiltersParam,
    division: FiltersParam,
    region: FiltersParam,
    legal_entity: FiltersParam,
    business_unit: FiltersParam,
    control_owner: FiltersParam
  });

  const getValidIdFromArray = (value: unknown): number | undefined => {
    if (!Array.isArray(value) || value.length === 0) return undefined;

    const num = Number(value[0]);
    return Number.isInteger(num) && num > 0 ? num : undefined;
  };

  const buildSingleControlParamFromQuery = () => {
    const params: Record<string, number> = {};

    const groupId = getValidIdFromArray(query.group);
    if (groupId !== undefined) {
      params.group_id = groupId;
    } else {
      const divisionId = getValidIdFromArray(query.division);
      if (divisionId !== undefined) {
        params.division_id = divisionId;
      } else {
        const regionId = getValidIdFromArray(query.region);
        if (regionId !== undefined) {
          params.region_id = regionId;
        } else {
          const legalEntityId = getValidIdFromArray(query.legal_entity);
          if (legalEntityId !== undefined) {
            params.legal_entity_id = legalEntityId;
          } else {
            const businessUnitId = getValidIdFromArray(query.business_unit);
            if (businessUnitId !== undefined) {
              params.business_unit_id = businessUnitId;
            }
          }
        }
      }
    }

    const controlOwnerId = getValidIdFromArray(query.control_owner);
    if (controlOwnerId !== undefined) {
      params.control_owner_id = controlOwnerId;
    }

    return params;
  };

  const scopeKey = useMemo(() => {
    const scope = buildSingleControlParamFromQuery();
    return JSON.stringify(scope);
  }, [query]);

  useEffect(() => {
    const fetchControls = async () => {
      try {
        const params = buildSingleControlParamFromQuery();
        const response = await ApiService.getControlsTestOperationalBPR(params);
        const apiControls = response.data.data;

        const mappedControls = apiControls.map((item: any) => {
          const instance = item.instances?.[0] ?? {};

          return {
            // ───────── CONTROL LEVEL ─────────
            key: item.id.toString(),
            id: item.id,
            active: item.active,
            comments: item.comments,
            controlDescription: item.control_description,
            control_image: item.control_image,
            controlName: item.control_name,
            keyControl: item.key_control || false,
            createdAt: item.created_at,
            deleted: item.deleted,
            endDate: item.end_date,
            notUsed: item.not_used === true,
            startDate: item.start_date,
            updatedAt: item.updated_at,

            testingMethod: {
              id: item?.testing_method_id_2 ?? null
            },

            testType: {
              id: item?.test_type?.id ?? null,
              name: item?.test_type?.name ?? null
            },

            // ───────── INSTANCE LEVEL ─────────

            testObjectiveness: instance.test_objectiveness
              ? typeof instance.test_objectiveness === 'string'
                ? instance.test_objectiveness
                : {
                    id: instance.test_objectiveness?.id ?? null,
                    name: instance.test_objectiveness?.name ?? null
                  }
              : null,

            performerToE: instance.performer_toe
              ? typeof instance.performer_toe === 'string'
                ? instance.performer_toe
                : {
                    id: instance.performer_toe?.id ?? null,
                    name: instance.performer_toe?.name ?? null
                  }
              : null,
            testResult: {
              id: instance?.test_result?.id ?? null,
              name: instance?.test_result?.name ?? null
            },

            evidenceType: {
              id: instance?.evidence_type?.id ?? null,
              name: instance?.evidence_type?.name ?? null
            },

            operatingEffectiveness: {
              id: instance?.operating_effectiveness?.id ?? null,
              name: instance?.operating_effectiveness?.name ?? null
            },

            designEffectiveness: {
              id: instance?.design_effectiveness?.id ?? null,
              name: instance?.design_effectiveness?.name ?? null
            },

            testDescription: instance?.test_description_2 ?? '',
            evidenceDescription: instance?.evidence_description_2 ?? '',
            evidenceFile2: instance?.evidence_file_2 ?? '',
            lastTestDateOperational: instance?.last_test_date_operational ?? '',
            nextTestDate: instance?.next_test_date ?? ''
          };
        });

        // Filter to show only key controls (where keyControl is true)
        const keyControlsOnly = mappedControls.filter(
          (control: any) => control.keyControl === true
        );

        setControls(keyControlsOnly);
        originalControlsRef.current = JSON.parse(
          JSON.stringify(keyControlsOnly)
        );
        setIsDirty(false);

        dispatch(internalControlsOp.setDrawTable(false));
      } catch (error) {
        console.error('Error fetching controls:', error);
      }
    };

    fetchControls();
  }, [drawTable, dispatch, scopeKey]);

  useEffect(() => {
    const fetchAllDropdowns = async () => {
      try {
        const [
          performerToERes,
          operatingEffRes,
          testingMethodRes,
          testTypeRes,
          testResultRes,
          evidenceTypeRes,
          testObjectivenessRes
        ] = await Promise.all([
          ApiService.getPerformerToEBPR(),
          ApiService.getOperatingEffectivenessBPR(),
          ApiService.getTestingMethodBPR(),
          ApiService.getTestTypesBPR(),
          ApiService.getTestResultBPR(),
          ApiService.getEvidenceTypeBPR(),
          ApiService.getTestObjectivenessBPR()
        ]);

        setPerformerToE(
          performerToERes.data.data.map((o: any) => ({
            label: o.name,
            value: o.id
          }))
        );
        setOperatingEffectiveness(
          operatingEffRes.data.data.map((o: any) => ({
            label: o.name,
            value: o.id
          }))
        );

        setTestingMethod(
          testingMethodRes.data.data.map((o: any) => ({
            label: o.name,
            value: o.id
          }))
        );

        setTestObjectiveness(
          testObjectivenessRes.data.data.map((o: any) => ({
            label: o.name,
            value: o.id
          }))
        );

        setTestType(
          testTypeRes.data.data.map((o: any) => ({
            label: o.name,
            value: o.id
          }))
        );

        setTestResult(
          testResultRes.data.data.map((o: any) => ({
            label: o.name,
            value: o.id
          }))
        );

        setEvidenceType(
          evidenceTypeRes.data.data.map((o: any) => ({
            label: o.name,
            value: o.id
          }))
        );
      } catch (err) {
        console.error('Failed to fetch dropdown data', err);
      }
    };

    fetchAllDropdowns();
  }, []);

  const buildNormalizedScope = (): QueryScope => {
    const singleScope = buildSingleControlParamFromQuery();
    return {
      group_id: singleScope.group_id ?? null,
      division_id: singleScope.division_id ?? null,
      region_id: singleScope.region_id ?? null,
      legal_entity_id: singleScope.legal_entity_id ?? null,
      business_unit_id: singleScope.business_unit_id ?? null
    };
  };

  const isScopedView = useMemo(() => {
    const scope = buildSingleControlParamFromQuery();
    return Object.keys(scope).length > 0;
  }, [query]);

  const controlColumns = useMemo(
    () =>
      getOperationalEffectivenessColumns(
        testObjectiveness,
        controls,
        performerToE,
        operatingEffectiveness,
        designEffectiveness,
        testingMethod,
        testType,
        testResult,
        evidenceType,
        setControls,
        trackingSetControls,
        newControlIds,
        isScopedView,
        t
      ),
    [
      testObjectiveness,
      controls,
      performerToE,
      operatingEffectiveness,
      designEffectiveness,
      testingMethod,
      testType,
      testResult,
      evidenceType,
      newControlIds,
      isScopedView,
      t
    ]
  );

  const preparePayload = (controlsPerRow: ControlRow[], scope: QueryScope) => {
    const hasScope = Object.values(scope).some(
      (value) => value !== null && value !== undefined
    );

    return {
      data_list: controlsPerRow.map((ctrl) => {
        const basePayload = {
          id: ctrl.id,
          control_name: ctrl.controlName || null,
          control_description: ctrl.controlDescription || null,
          control_image: ctrl.controlImage || null,
          testing_method_2: ctrl.testingMethod?.id ?? null,
          start_date: ctrl.startDate
            ? dayjs(ctrl.startDate).format('YYYY-MM-DD')
            : null,
          end_date: ctrl.endDate
            ? dayjs(ctrl.endDate).format('YYYY-MM-DD')
            : null,
          active: ctrl.active,
          comments: ctrl.comments,
          test_type_id: ctrl.testType?.id ?? null
        };

        if (!hasScope) {
          return basePayload;
        }

        return {
          ...basePayload,
          instance: {
            group: scope.group_id ?? null,
            division: scope.division_id ?? null,
            region: scope.region_id ?? null,
            legal_entity: scope.legal_entity_id ?? null,
            business_unit: scope.business_unit_id ?? null,
            performer_toe:
              typeof ctrl.performerToE === 'string'
                ? ctrl.performerToE
                : ctrl.performerToE?.id ?? null,
            operating_effectiveness: ctrl.operatingEffectiveness?.id ?? null,
            last_test_date_operational: ctrl.lastTestDateOperational
              ? dayjs(ctrl.lastTestDateOperational).format('YYYY-MM-DD')
              : null,
            next_test_date: ctrl.nextTestDate
              ? dayjs(ctrl.nextTestDate).format('YYYY-MM-DD')
              : null,
            test_description_2: ctrl?.testDescription ?? '',
            test_objectiveness:
              typeof ctrl.testObjectiveness === 'string'
                ? ctrl.testObjectiveness
                : ctrl.testObjectiveness?.id?.toString() ?? null,
            test_result: ctrl.testResult?.id ?? null,
            evidence_description_2: ctrl.evidenceDescription || null,
            evidence_type: ctrl.evidenceType?.id ?? null,
            evidence_file_2: ctrl.evidenceFile2 || null,
            assertion_area_3_id: 3
          }
        };
      })
    };
  };

  const handleControlSave = async () => {
    try {
      if (!isDirty && newControlIds.size === 0) {
        notification.info({
          message: 'No Changes',
          description: 'No changes detected to save.',
          placement: 'topRight',
          duration: 3
        });
        return;
      }

      // Find changed controls by comparing with original
      const changedControls = controls.filter((control) => {
        if (newControlIds.has(control.id)) return true;
        const original = originalControlsRef.current.find(
          (c) => c.id === control.id
        );
        if (!original) return false;

        // Check if control has actually changed
        return JSON.stringify(control) !== JSON.stringify(original);
      });

      if (changedControls.length === 0) {
        notification.info({
          message: 'No Changes',
          description: 'No changes detected to save.',
          placement: 'topRight',
          duration: 3
        });
        return;
      }

      const scope = buildNormalizedScope();
      const payload = preparePayload(changedControls, scope);
      await ApiService.saveControlsTestoperationalBPR(payload);

      originalControlsRef.current = JSON.parse(JSON.stringify(controls));
      setNewControlIds(new Set());
      setIsDirty(false);
      dispatch(internalControlsOp.setHasChanges(false));
      dispatch(internalControlsOp.setDrawTable(true));

      notification.success({
        message: 'Controls Saved',
        description: `${changedControls.length} control(s) saved successfully.`,
        placement: 'topRight',
        duration: 3
      });
    } catch (error: unknown) {
      console.error('Save error:', error);

      const errorMessage =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ||
        (error as Error)?.message ||
        'Unknown error occurred';
      const statusCode =
        (error as { response?: { status?: unknown } })?.response?.status ||
        'Unknown';

      notification.error({
        message: `Save Failed (${statusCode})`,
        description: errorMessage,
        placement: 'topRight',
        duration: 10
      });
    }
  };

  return (
    <InternalControlLayout onSave={handleControlSave}>
      <Styled.OperationalEffectivenessWrapper>
        <CustomTable
          drawTable={true}
          rowKey='key'
          columns={controlColumns}
          dataSource={controls}
          itemsCount={controls.length}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total: number, range: [number, number]) =>
              `${range[0]}-${range[1]} of ${total} items`
          }}
          rowClassName={(record: { notUsed: boolean }) =>
            record.notUsed ? 'disabled-row has-row-tooltip' : ''
          }
          onRow={(record: { notUsed: boolean }) => {
            if (!record.notUsed) return {};

            return {
              'data-tooltip':
                'Disabled due to a previous Risk/Control type change.'
            };
          }}
          scroll={{ x: 2500 }}
        />
      </Styled.OperationalEffectivenessWrapper>
    </InternalControlLayout>
  );
};

export default OperationalEffectivenessTesting;
